// Material 3 Card: elevated, filled and outlined variants.
// Colors, shape, elevation and state-layer opacities all come from the --m3-* tokens (shared/m3.css + M3Core).
// Clickable cards behave as buttons (role, tabIndex, Enter/Space) and get hover/focus/press state layers.
(function () {
  const { Card: MuiCard, CardContent, CardActions } = MaterialUI;

  const motion = 'var(--m3-duration-short) var(--m3-easing-standard)';

  const variants = {
    elevated: {
      backgroundColor: 'var(--m3-surface-container-low)',
      border: 'none',
      boxShadow: 'var(--m3-elevation-1)',
      hoverShadow: 'var(--m3-elevation-2)'
    },
    filled: {
      backgroundColor: 'var(--m3-surface-container-highest)',
      border: 'none',
      boxShadow: 'none',
      hoverShadow: 'var(--m3-elevation-1)'
    },
    outlined: {
      backgroundColor: 'var(--m3-surface)',
      border: '1px solid var(--m3-outline-variant)',
      boxShadow: 'none',
      hoverShadow: 'none'
    }
  };

  function Card({
    variant = 'outlined', // 'elevated' | 'filled' | 'outlined'
    clickable = false,
    disabled = false,
    className = '',
    children,
    sx,
    onClick,
    onKeyDown,
    ...props
  }) {
    const v = variants[variant] || variants.outlined;
    const interactive = clickable && !disabled;

    const base = {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 'var(--m3-shape-medium)',
      backgroundColor: v.backgroundColor,
      backgroundImage: 'none',
      border: v.border,
      boxShadow: v.boxShadow,
      color: 'var(--m3-on-surface)',
      transition: `box-shadow ${motion}, background-color ${motion}`,
      // State layer: on-surface overlay whose opacity follows the interaction state.
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: 1,
        pointerEvents: 'none',
        backgroundColor: 'var(--m3-on-surface)',
        opacity: 0,
        transition: `opacity ${motion}`
      }
    };

    if (interactive) {
      Object.assign(base, {
        cursor: 'pointer',
        outline: 'none',
        '&:hover': { boxShadow: v.hoverShadow },
        '&:hover::before': { opacity: 'var(--m3-state-hover)' },
        '&:focus-visible::before': { opacity: 'var(--m3-state-focus)' },
        '&:focus-visible': { outline: '3px solid var(--m3-secondary)', outlineOffset: '2px' },
        '&:active::before': { opacity: 'var(--m3-state-pressed)' }
      });
    }

    if (disabled) {
      base.opacity = 0.38;
      base.pointerEvents = 'none';
      base.boxShadow = 'none';
    }

    const interactiveProps = interactive ? {
      role: 'button',
      tabIndex: 0,
      onClick,
      onKeyDown: (e) => {
        if (onKeyDown) onKeyDown(e);
        if (e.defaultPrevented || e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onClick) onClick(e);
        }
      }
    } : { onClick, onKeyDown };

    return (
      <MuiCard
        elevation={0}
        className={`m3-card ${className}`.trim()}
        aria-disabled={disabled || undefined}
        sx={[base, ...(Array.isArray(sx) ? sx : [sx])]}
        {...interactiveProps}
        {...props}
      >
        {children}
      </MuiCard>
    );
  }

  const ElevatedCard = (props) => <Card variant="elevated" {...props} />;
  const FilledCard = (props) => <Card variant="filled" {...props} />;
  const OutlinedCard = (props) => <Card variant="outlined" {...props} />;

  window.Components = window.Components || {};
  window.Components.Card = Card;
  window.Components.ElevatedCard = ElevatedCard;
  window.Components.FilledCard = FilledCard;
  window.Components.OutlinedCard = OutlinedCard;
  window.Components.CardContent = CardContent;
  window.Components.CardActions = CardActions;
})();
